import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import pluralize from 'pluralize';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import Draggable from 'react-draggable';
import { Button } from 'react-bootstrap';
import withChartExport from '../../../components/ChartExporting';

import highchartFactory from '../../../util/highcharts';
import { percentileGroupText } from '../../../util/percentileTrends';
const highchartUtils = highchartFactory(Highcharts);

import { saveConfig, updateLineChart } from '../../../components/Filter/FilterActions';
import dateUtils from '../../../util/dates';
import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import ChartDownloadMenu from '../../../components/ChartDownloadMenu';
import { ViewSwitch, CardList, rangeTitle } from '../../../components/CardViews/CardViews';
import legendIcon from '../../../assets/img/legend.svg';
import lineIcon from '../../../assets/img/chart-line.svg';
import chartUtils from '../../../util/chartUtils';
import { PERCENTILE_LABEL_COLORS } from '../../../util/colors';

import './PercentilesTrendChart.scss';

function spreadEndLabels(chart) {
  const ends = _(chart.series)
    .filter('visible')
    .map(series => _.last(series.points))
    .filter(point => point && point.dataLabel)
    .sortBy('plotY')
    .value();
  const height = point => point.dataLabel.getBBox().height;
  const blocks = [];
  ends.forEach(point => {
    const top = point.plotY - height(point) / 2;
    let block = { points: [point], height: height(point), sum: top, top };
    while (blocks.length && _.last(blocks).top + _.last(blocks).height > block.top) {
      const above = blocks.pop();
      const sum = above.sum + block.sum - block.points.length * above.height;
      const points = above.points.concat(block.points);
      block = { points, height: above.height + block.height, sum, top: sum / points.length };
    }
    blocks.push(block);
  });
  const shifts = new Map();
  blocks.forEach(block => {
    let top = Math.min(block.top, chart.plotHeight - block.height);
    block.points.forEach(point => {
      shifts.set(point, Math.round(top - (point.plotY - height(point) / 2)));
      top += height(point);
    });
  });
  // redraw fires render again; the offsets come from the lines' ends, so that second pass finds nothing to move
  if (ends.some(point => ((point.options.dataLabels || {}).y || 0) !== shifts.get(point))) {
    ends.forEach(point => point.update({ dataLabels: { y: shifts.get(point) } }, false));
    chart.redraw(false);
  }
}

const config = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff',
    marginRight: 40,
    events: {
      render: function() {
        spreadEndLabels(this);
      }
    }
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  tooltip: {
    // Duplicate x values require point-based hover
    shared: false,
    useHTML: true,
    // The box hides anything outside its edges, and this tooltip is taller than the box
    outside: true,
    backgroundColor: '#ffffff',
    borderColor: '#d5e7f2',
    borderRadius: 8
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    showFirstLabel: true,
    showLastTickLabel: true,
    title: {
      text: null
    }
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false,
    showLastTickLabel: false
  },
  plotOptions: {
    spline: {
      lineWidth: 1.5,
      findNearestPointBy: 'xy',
      marker: {
        symbol: 'circle',
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 3
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      },
      dataLabels: {
        enabled: true,
        align: 'left',
        verticalAlign: 'middle',
        x: 8,
        y: 0,
        crop: false,
        overflow: 'allow',
        formatter: function() {
          return this.point.index === this.series.points.length - 1
            ? `<span style="color: ${PERCENTILE_LABEL_COLORS[this.series.name] || this.series.color}">${this.series.name}%</span>`
            : null;
        },
        style: {
          fontSize: '12px',
          fontWeight: '600',
          textOutline: 'none'
        }
      }
    }
  },
  series: [
    {
      data: []
    }
  ]
};

const DEFAULT_POS = { x: 95, y: 85 };
const SELECTED_GROUP_SERIES_ID = 'selected-group';
const CLICK_HINT = '<div class="percentile-trends-tooltip__hint">Click to view items</div>';

const percentileRow = point =>
  `<div class="tooltip-label-item">` +
  `<span class="label-box" style="background: ${point.color}"></span>` +
  `${point.series.name}%: ${pluralize('day', point.y, true)}` +
  `</div>`;

const toRows = texts => texts.map(text => `<div>${text}</div>`).join('');

export class PercentilesTrendChart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isFullSreen: false,
      isLabelsVisible: false,
      isListView: !props.wallmode,
      listRows: [],
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS
    };
  }

  static defaultProps = {
    data: {},
    groups: []
  };

  componentDidMount() {
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    config.exporting = { ...config.exporting, ...this.props.chartExporting };

    const that = this;
    config.tooltip.formatter = function() {
      return that.tooltipFormatter(this.point.index);
    };
    config.plotOptions.spline.point = {
      events: {
        click() {
          that.handlePointClick(this);
        }
      }
    };
    config.chart.events.click = function() {
      if (this.hoverPoint) {
        that.handlePointClick(this.hoverPoint);
      }
    };

    this.chart = new Highcharts['Chart'](`avg_chart_container`, config);

    this.updateDateFormat();
    this.calculateChartData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.overall !== this.props.overall) {
      this.updateListRows();
    }
    if (!_.isEqual(prevProps.data, this.props.data) || !_.isEqual(prevProps.groups, this.props.groups)) {
      this.calculateChartData();
    }
    if (!_.isEqual(prevProps.percentiles, this.props.percentiles)) {
      this.calculateChartData();
    }
    if (prevProps.selectedGroupIndex !== this.props.selectedGroupIndex) {
      this.drawSelectedGroup();
      this.chart.redraw();
    }
    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateDateFormat();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateDateFormat() {
    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: dateUtils.createHighchartFormats(this.props.dateFormat)
      }
    });
  }

  tooltipFormatter(index) {
    const { groups, groupsSettings } = this.props;
    const { title, aboveRows, belowRows } = percentileGroupText(groups[index], groupsSettings);
    const lines = _.reject(this.chart.series, { options: { id: SELECTED_GROUP_SERIES_ID } });
    const rows = _.orderBy(lines, series => Number(series.name), 'desc').map(series => percentileRow(series.points[index]));
    const hint = this.props.onGroupClick ? CLICK_HINT : '';

    return `<div class="percentile-trends-tooltip"><b>${title}</b>${toRows(aboveRows)}${rows.join('')}${toRows(belowRows)}${hint}</div>`;
  }

  handlePointClick(point) {
    if (this.props.onGroupClick) {
      this.props.onGroupClick(point.index);
    }
  }

  lineOptions(line) {
    const isIncomplete = index => this.props.groups[index].incomplete;

    return {
      ...line,
      className: 'percentile-trends-line',
      cursor: this.props.onGroupClick ? 'pointer' : undefined,
      zoneAxis: 'x',
      zones: _.map(_.tail(line.data), (point, i) => ({
        value: point.x,
        dashStyle: isIncomplete(i + 1) || isIncomplete(i) ? 'Dash' : 'Solid'
      })),
      data: _.map(line.data, (point, index) => (
        isIncomplete(index) ? { ...point, className: 'percentile-trends-hollow', marker: { enabled: true, lineColor: line.color } } : point
      ))
    };
  }

  drawSelectedGroup() {
    const { data, percentiles, selectedGroupIndex } = this.props;
    const selectedGroupSeries = this.chart.get(SELECTED_GROUP_SERIES_ID);
    if (selectedGroupSeries) {
      selectedGroupSeries.remove(false);
    }
    if (_.isNil(selectedGroupIndex)) {
      return;
    }

    const selectedLines = _.filter(data, line => _.includes(percentiles, line.name));
    this.chart.addSeries({
      id: SELECTED_GROUP_SERIES_ID,
      type: 'scatter',
      enableMouseTracking: false,
      showInLegend: false,
      states: { inactive: { enabled: false } },
      marker: { symbol: 'circle', radius: 9, fillColor: 'transparent', lineWidth: 2 },
      data: _.map(selectedLines, line => {
        const { x, y } = line.data[selectedGroupIndex];
        return { x, y, marker: { lineColor: line.color } };
      })
    }, false);
  }

  calculateChartData() {
    const { data } = this.props;

    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    for (let i = this.props.percentiles.length - 1; i >= 0; i--) {
      const pObj = _.find(data, item => item.name == this.props.percentiles[i]);
      if (!pObj) {
        continue;
      }
      this.chart.addSeries(chartUtils.trendLineSeries(this.lineOptions(pObj), false), false);
    }

    this.drawSelectedGroup();
    this.chart.redraw();
    this.updateListRows();
  }

  updateFontSize = () => {
    this.chart.update({
      exporting: {
        enabled: this.state.isFullSreen,
        chartOptions: {
          title: {
            text: 'Percentiles',
            style: {
              fontSize: '16px',
              fontWeight: 'bold'
            }
          },          
        }
      },

      legend: {
        ...chartUtils.cardLegend(this.state.isFullSreen),
        enabled: this.state.isLabelsVisible,
        useHTML: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        labelFormatter: function () {
          return `
            <span class="color:#333333;font-size:12px;font-weight:normal">${this.name}%</span>
          `;
        }
      },
      
      yAxis: {
        gridLineWidth: +this.state.isFullSreen,
        labels: {
          enabled: this.state.isFullSreen
        }
      }
    }, true, false, false);

    this.chart.reflow();
  };

  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  updateListRows() {
    const colors = _.fromPairs((this.chart ? this.chart.series : []).map(series => [String(series.name), series.color]));
    const rows = [...(this.props.percentiles || [])]
      .sort((a, b) => b - a)
      .map(percentile => {
        const item = _.find(this.props.overall || [], entry => entry.name == percentile);
        const value = item ? chartUtils.daysInUnit(item.value, this.props.timeUnit) : null;
        return {
          name: `${percentile}%`,
          color: colors[String(percentile)],
          values: [value === null ? '---' : `${value} ${this.props.timeUnit}${value != 1 ? 's' : ''}`]
        };
      });
    this.setState({ listRows: rows.some(row => row.values[0] !== '---') ? rows : [] });
  }

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          left: undefined,
          isFullSreen: false,
          isLabelsVisible: this.labelsBeforeFocus,
          pos: DEFAULT_POS
        },
        () => this.updateFontSize(false)
      );
    } else {
      this.labelsBeforeFocus = this.state.isLabelsVisible;
      this.setState(
        {
          width: '100%',
          height: '100%',
          left: -10,
          isFullSreen: true,
          isLabelsVisible: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize(true)
      );
    }
  };

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box long_title ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{
            width: this.state.width,
            height: this.state.height,
            left: this.state.left,
            position: this.state.isFullSreen ? 'absolute' : ''
          }}
        >
          <div className="box_white_title">
            <h3>Percentiles</h3>
            <div className='right-actions'>
              <ViewSwitch
                views={[{ id: 'list', icon: legendIcon }, { id: 'line', icon: lineIcon }]}
                active={this.state.isListView ? 'list' : 'line'}
                onChange={view => this.setState({ isListView: view === 'list' })}
              />
              <Button variant='secondary' size='sm' className={`btn-action ${this.state.isFullSreen ? 'fullscreen' : ''}`} onClick={this.handleResize}>
                <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              {/* in focus the chart can be downloaded, from the header like the card's other actions */}
              {this.state.isFullSreen && <ChartDownloadMenu getChart={() => this.chart} />}
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          <div id="avg_chart_container" />
          {/* with no line to draw the card shows its empty list, pulsing while the page loads, instead of a chart that
              waits for lines that never come */}
          {(this.state.isListView || !this.props.data.length) && (
            <CardList
              columns={[rangeTitle(this.props.dates, this.props.dateFormat)]}
              rows={this.state.isListView ? this.state.listRows : []}
              loading={this.props.isLoading}
            />
          )}
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateLineChart(false, 'ctsp')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    wallmode: state.app.wallmode,
    dateFormat: state.filters.dateFormat,
    percentiles: state.filters.ctsp.selectedPercentiles,
    timeUnit: state.filters.ctsp.timeUnit,
    overall: state.ctsp.data && state.ctsp.data.percentiles,
    dates: state.filters.dates,
    isLoading: state.ctsp.isLoading,
    groupsSettings: state.ctsp.percentileGroupsSettings
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(PercentilesTrendChart));
